import { useEffect, useState } from "react";
import { FeatureFlagClient } from "./feature-flag-client";
import type { FeatureFlagsOptions } from "./types";

/**
 * Binds a {@link FeatureFlagClient} to the hooks that read it, so an app
 * configures Flipt once in a helper module and imports the result everywhere
 * else — no provider to mount and nothing for tests to set up.
 */
export const createFeatureFlags = (options: FeatureFlagsOptions) => {
	const client = new FeatureFlagClient(options);

	const useFeatureFlag = (flagKey: string) => {
		const [isEnabled, setIsEnabled] = useState(false);
		const [isLoading, setIsLoading] = useState(true);

		useEffect(() => {
			let isCancelled = false;

			client.isEnabled(flagKey).then((enabled) => {
				if (isCancelled) {
					return;
				}
				setIsEnabled(enabled);
				setIsLoading(false);
			});

			return () => {
				isCancelled = true;
			};
		}, [flagKey]);

		return { isEnabled, isLoading };
	};

	const useFeatureFlags = <K extends string>(flagKeys: readonly K[]) => {
		const [flags, setFlags] = useState<Partial<Record<K, boolean>>>();
		const [isLoading, setIsLoading] = useState(true);

		// Keyed on the contents rather than the array: callers pass an inline
		// literal, which is a new reference on every render.
		const flagKey = flagKeys.join(",");

		useEffect(() => {
			let isCancelled = false;

			client.getEnabledFlags(flagKey.split(",") as K[]).then((enabled) => {
				if (isCancelled) {
					return;
				}
				setFlags(enabled);
				setIsLoading(false);
			});

			return () => {
				isCancelled = true;
			};
		}, [flagKey]);

		return { flags, isLoading };
	};

	return { client, useFeatureFlag, useFeatureFlags };
};

export type FeatureFlags = ReturnType<typeof createFeatureFlags>;
